import { Box, Button, Collapse, Group, Paper } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getMinimalMRTOptions } from "../../../../../../../../mantine/getMinimalMRTOptions";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import type { ChangeableFieldErrors } from "../../../../../../../ChangeableFieldWidget/ChangeableFieldTable/ChangeableFieldTable";
import type {
  ChangeableFieldData,
  ChangeableFieldWidgetOptions
} from "../../../../../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableFieldWidget } from "../../../../../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { FuturePriceWarningModal } from "../../../../../SharedFields/ChangeablePriceField/FuturePriceWarningModal/FuturePriceWarningModal";

const CHANGEABLE_FIELD_WIDGET_OPTIONS: ChangeableFieldWidgetOptions = {
  addButtonText: "Weitere Formel hinzufügen",
  addNullStartDateWhenNoValues: true,
  endDatePlaceholder: "-",
  startDatePlaceholder: "-",
  deleteTooltipText: "Formel entfernen",
  numberValueHeader: "Formel",
  inputGroupText: "ct/kWh",
  saveButtonText: "Übernehmen",
  canAddFutureDates: true
};
export interface ChangeableSpotMarketFormula {
  id?: number;
  firstDate: string | null;
  lastDate: string | null;
  formula: string;
}

export interface ChangeableSpotMarketFormulaFieldStaticProps {
  allowInput?: boolean;
  fieldName: string;
  unit: string;
  value: Array<ChangeableSpotMarketFormula>;
  collapsible?: boolean;
  errors?: ChangeableFieldErrors;
  ref?: React.Ref<HTMLDivElement>;
  onInput: (name: string, value: Array<ChangeableSpotMarketFormula>) => void;
}

function ChangeableSpotMarketFormulaFieldStatic({
  allowInput,
  fieldName,
  value: formulas = [],
  unit,
  onInput,
  collapsible = true,
  errors,
  ref
}: ChangeableSpotMarketFormulaFieldStaticProps) {
  const [isHistoryCollapseOpen, setIsHistoryCollapseOpen] = useState(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [formulaDataForModal, setFormulaDataForModal] =
    useState<ChangeableSpotMarketFormula[]>();

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      setIsCollapseOpen(true);
    }
  }, [errors]);

  const optionsWithUnit = useMemo(() => {
    return {
      ...CHANGEABLE_FIELD_WIDGET_OPTIONS,
      inputGroupText: unit,
      inputGroupTextWidth: 60,
      collapsible,
      isTextField: true
    };
  }, [collapsible, unit]);

  function handleClickHistoryButton() {
    setIsCollapseOpen(false);
    setIsHistoryCollapseOpen((open) => !open);
  }

  function handleClickEditButton() {
    setIsHistoryCollapseOpen(false);
    setIsCollapseOpen((open) => !open);
  }

  function getEarliestDate(noIdObjects: Array<ChangeableSpotMarketFormula>) {
    return noIdObjects.reduce((prev, curr) => {
      if (prev.firstDate === null) {
        return prev;
      } else if (curr.firstDate === null) {
        return curr;
      }

      const prevDate = backendDateOrDateTimeToLuxonDateTime(prev.firstDate);
      const currDate = backendDateOrDateTimeToLuxonDateTime(curr.firstDate);

      return prevDate < currDate ? prev : curr;
    });
  }
  const handleSaveFormulas = useCallback(
    (formulas: Array<ChangeableSpotMarketFormula>) => {
      setShowWarningModal(false);
      onInput(fieldName, formulas);
      setIsCollapseOpen(false);
      return Promise.resolve();
    },
    [onInput, fieldName]
  );

  const convertChangeableFieldDataToFormulas = useCallback(
    (values: Array<ChangeableFieldData<string>>) => {
      return values.map<ChangeableSpotMarketFormula>((value) => ({
        id: value.id,
        formula: value.value as unknown as string,
        firstDate: value.startDate
          ? luxonDateTimeToBackendDateOrDateTime(value.startDate)
          : null,
        lastDate: value.endDate
          ? luxonDateTimeToBackendDateOrDateTime(value.endDate)
          : null
      }));
    },
    []
  );

  const submitFormulas = useCallback(
    (changeableFieldData: Array<ChangeableFieldData<string | null>>) => {
      if (changeableFieldData.some((value) => value.value === null)) {
        return Promise.reject("Alle Formeländerungen müssen einen Wert haben.");
      }

      const newFormulas = convertChangeableFieldDataToFormulas(
        changeableFieldData as Array<ChangeableFieldData<string>>
      );
      const noIdObjects = newFormulas.filter((obj) => obj.id === undefined);
      const oneMonthFromNow = DateTime.now().plus({ months: 1 });

      if (noIdObjects && noIdObjects.length > 0) {
        const firstNewDate = getEarliestDate(noIdObjects);
        const dateToCheck = firstNewDate.firstDate
          ? backendDateOrDateTimeToLuxonDateTime(firstNewDate.firstDate)
          : firstNewDate.lastDate
            ? backendDateOrDateTimeToLuxonDateTime(firstNewDate.lastDate)
            : null;

        if (dateToCheck && dateToCheck < oneMonthFromNow) {
          setShowWarningModal(true);
          setFormulaDataForModal(newFormulas);
          return Promise.resolve();
        }
      }

      return handleSaveFormulas(newFormulas);
    },
    [handleSaveFormulas, convertChangeableFieldDataToFormulas]
  );

  function convertFormulasToChangeableFieldData(
    formulas?: Array<ChangeableSpotMarketFormula>
  ) {
    if (!formulas) {
      return undefined;
    }

    return formulas
      .map<ChangeableFieldData<string>>((formula) => ({
        id: formula.id,
        startDate: formula.firstDate
          ? backendDateOrDateTimeToLuxonDateTime(formula.firstDate)
          : null,
        endDate: formula.lastDate
          ? backendDateOrDateTimeToLuxonDateTime(formula.lastDate)
          : null,
        value: formula.formula
      }))
      .sort((a, b) => {
        if (a.startDate === null && b.startDate === null) {
          return 0;
        } else if (a.startDate === null) {
          return -1;
        } else if (b.startDate === null) {
          return 1;
        } else if (a.startDate < b.startDate) {
          return -1;
        } else if (b.startDate < a.startDate) {
          return 1;
        }

        return 0;
      });
  }

  function getCurrentFormula(formulas: Array<ChangeableSpotMarketFormula>) {
    const now = DateTime.now();

    return formulas.find((formula) => {
      const firstDate = formula.firstDate
        ? backendDateOrDateTimeToLuxonDateTime(formula.firstDate)
        : null;
      const lastDate = formula.lastDate
        ? backendDateOrDateTimeToLuxonDateTime(formula.lastDate)
        : null;

      const firstDateInRange = !firstDate || firstDate <= now;
      const lastDateInRange = !lastDate || now <= lastDate;

      return firstDateInRange && lastDateInRange;
    });
  }

  const currentFormula = useMemo(() => {
    return getCurrentFormula(formulas);
  }, [formulas]);

  const suggestedChangeableFormulas = useMemo(() => {
    return convertFormulasToChangeableFieldData(formulas);
  }, [formulas]);

  const memoizedChangeableFieldWidget = useMemo(() => {
    if (!suggestedChangeableFormulas) {
      return null;
    }
    return (
      <ChangeableFieldWidget
        dataName="Formeländerungen"
        errors={errors}
        initialValues={suggestedChangeableFormulas}
        options={optionsWithUnit}
        onCancel={() => {
          setIsCollapseOpen(false);
        }}
        onDone={submitFormulas}
      />
    );
  }, [errors, optionsWithUnit, submitFormulas, suggestedChangeableFormulas]);

  return (
    <div className="ChangeableSpotMarketFormulaFieldStatic" ref={ref}>
      {showWarningModal && formulaDataForModal && (
        <FuturePriceWarningModal
          prices={formulaDataForModal}
          onCancel={() => setShowWarningModal(false)}
          onSuccess={handleSaveFormulas}
        />
      )}
      <Group mb="xs">
        <Button
          disabled={!optionsWithUnit.collapsible}
          flex={1}
          justify="space-between"
          leftSection={
            isHistoryCollapseOpen ? (
              <IconChevronDown size="1.4em" strokeWidth={1} />
            ) : (
              <IconChevronRight size="1.4em" strokeWidth={1} />
            )
          }
          rightSection="ct/kWh"
          styles={{ label: { flex: 1 } }}
          variant="default"
          onClick={handleClickHistoryButton}
        >
          {currentFormula?.formula}
        </Button>
        <Button
          disabled={!allowInput || isCollapseOpen}
          onClick={handleClickEditButton}
        >
          Formeländerung hinzufügen
        </Button>
      </Group>
      <Collapse expanded={isHistoryCollapseOpen}>
        {formulas && <FormulaHistory formulas={formulas} />}
      </Collapse>
      <Collapse expanded={isCollapseOpen}>
        <Paper bd="1px solid neutral.3" mt="xs" shadow="none">
          {memoizedChangeableFieldWidget}
        </Paper>
      </Collapse>
    </div>
  );
}

interface FormulaHistoryProps {
  formulas: Array<ChangeableSpotMarketFormula>;
}

function FormulaHistory({ formulas }: FormulaHistoryProps) {
  const columns: Array<MRT_ColumnDef<ChangeableSpotMarketFormula>> = useMemo(
    () => [
      {
        header: "Von",
        accessorFn: (rowData) => rowData.firstDate ?? "- ",
        size: 85
      },
      {
        header: "Bis",
        accessorFn: (rowData) => rowData.lastDate ?? "-",
        size: 85
      },
      { header: "Formel", accessorFn: (rowData) => rowData.formula }
    ],
    []
  );

  const mrtTable = useMantineReactTable<ChangeableSpotMarketFormula>({
    ...getMinimalMRTOptions(),
    columns: columns,
    data: formulas
  });

  return (
    <Box mt="xs">
      <MantineReactTable table={mrtTable} />
    </Box>
  );
}

export { ChangeableSpotMarketFormulaFieldStatic };
